import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Sparkles, 
  Flame, 
  PackageCheck, 
  Zap, 
  Shield, 
  Crown, 
  Coins, 
  Palette, 
  CheckCircle2, 
  Info,
  Gift,
  Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import canvasConfetti from 'canvas-confetti';
import { 
  BioBoxInventoryItem, 
  BioBoxRarity, 
  LootItem, 
  UnboxingState, 
  Language, 
  Vibe 
} from '../types';
import { 
  BIO_BOX_DEFINITIONS, 
  RARITY_COLORS 
} from '../data/gamificationEconomyConfig';
import { executeUnboxBioBox } from '../services/bioBoxService';
import { sfx } from '../services/audioSynthesis';
import { auth } from '../services/firebase';
import { cn } from '../lib/utils';

const localConfetti = canvasConfetti.create(null, { useWorker: false, resize: true });

interface BioBoxUnboxingModalProps {
  isOpen: boolean;
  onClose: () => void;
  crate?: BioBoxInventoryItem | null;
  box?: BioBoxInventoryItem | null;
  boxes?: BioBoxInventoryItem[];
  userId?: string;
  lang: Language;
  vibe: Vibe | string;
  tone: 'bro' | 'system';
  theme?: 'light' | 'dark';
  skin?: 'default' | 'cyberpunk' | 'anime' | 'stalker' | string;
  userLuckModifier?: number;
  onCrateOpened?: (payout: { rarity: BioBoxRarity; tokenPayout: number; items: LootItem[] }) => void;
  onBoxOpened?: (payout: { rarity: BioBoxRarity; tokenPayout: number; items: LootItem[] }) => void;
}

export const BioBoxUnboxingModal: React.FC<BioBoxUnboxingModalProps> = ({
  isOpen,
  onClose,
  crate,
  box,
  boxes,
  userId,
  lang,
  vibe,
  tone,
  theme = 'dark',
  skin = 'default',
  userLuckModifier = 1.0,
  onCrateOpened,
  onBoxOpened
}) => {
  const [internalQueue, setInternalQueue] = useState<BioBoxInventoryItem[]>([]);
  const [currentQueueIndex, setCurrentQueueIndex] = useState<number>(0);
  const [state, setState] = useState<UnboxingState>('INSPECTING');
  const [payout, setPayout] = useState<{ rarity: BioBoxRarity; tokenPayout: number; items: LootItem[] } | null>(null);
  const [chargeProgress, setChargeProgress] = useState<number>(0);
  const [showOdds, setShowOdds] = useState<boolean>(false);

  const isUA = lang === 'ua' || (lang as string).startsWith('uk');
  const isStalker = skin === 'stalker';
  const isLight = theme === 'light' && !isStalker;

  const prevIsOpenRef = useRef(false);
  const chargeIntervalRef = useRef<any>(null);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened: initialize unboxing queue safely
      let initialQueue: BioBoxInventoryItem[] = [];
      const primary = box || crate;
      if (primary) {
        initialQueue.push(primary);
        if (boxes && boxes.length > 0) {
          const others = boxes.filter(b => b.id !== primary.id);
          initialQueue.push(...others);
        }
      } else if (boxes && boxes.length > 0) {
        initialQueue = [...boxes];
      }
      setInternalQueue(initialQueue);
      setCurrentQueueIndex(0);
      setState('INSPECTING');
      setPayout(null);
      setChargeProgress(0);
      setShowOdds(false);
    } else if (!isOpen && prevIsOpenRef.current) {
      // Clean up charge interval if modal closed during charge
      if (chargeIntervalRef.current) {
        clearInterval(chargeIntervalRef.current);
        chargeIntervalRef.current = null;
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (chargeIntervalRef.current) {
        clearInterval(chargeIntervalRef.current);
        chargeIntervalRef.current = null;
      }
    };
  }, []);

  const activeBox = internalQueue[currentQueueIndex] || box || crate;

  if (!isOpen || !activeBox) return null;

  const boxType = activeBox.boxType || activeBox.crateType || 'daily_supply';
  const boxMeta = BIO_BOX_DEFINITIONS[boxType] || BIO_BOX_DEFINITIONS.daily_supply;

  const handleStartUnboxing = async () => {
    if (state !== 'INSPECTING') return;

    setState('PRE_CHARGE');

    const startTime = Date.now();
    const duration = 1900;

    if (chargeIntervalRef.current) clearInterval(chargeIntervalRef.current);

    chargeIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, Math.round((elapsed / duration) * 100));
      setChargeProgress(progress);

      // Light tactile vibration pulses during build-up
      if (progress % 25 === 0 && 'vibrate' in navigator) {
        try { navigator.vibrate(12); } catch (e) {}
      }

      if (elapsed >= duration) {
        if (chargeIntervalRef.current) {
          clearInterval(chargeIntervalRef.current);
          chargeIntervalRef.current = null;
        }
        triggerImpactAndReveal();
      }
    }, 25);
  };

  const triggerImpactAndReveal = async () => {
    if (!activeBox) return;

    // 1. Impact Flash Frame + Heavy Haptics
    setState('IMPACT_FLASH');
    if ('vibrate' in navigator) {
      try { navigator.vibrate([40, 30, 80]); } catch (e) {}
    }

    const effectiveUid = userId || auth.currentUser?.uid || localStorage.getItem('last_user_id') || 'local_user';

    try {
      const result = await executeUnboxBioBox(effectiveUid, activeBox, userLuckModifier);
      setPayout(result);

      setTimeout(() => {
        // 2. Reveal State
        setState('REVEALING');

        // Particle Burst Palettes
        const particleColors = isStalker 
          ? ['#4ade80', '#22c55e', '#f97316', '#ef4444', '#a3e635']
          : ['#10B981', '#34D399', '#6EE7B7', '#F59E0B', '#8B5CF6'];

        // Center burst
        localConfetti({
          particleCount: result.rarity === 'mythic' ? 140 : result.rarity === 'legendary' ? 90 : 60,
          spread: 80,
          origin: { y: 0.5 },
          colors: particleColors,
          ticks: 200,
          gravity: 1.1
        });

        // Left and Right angled cannons for higher rarities
        if (result.rarity === 'epic' || result.rarity === 'legendary' || result.rarity === 'mythic') {
          setTimeout(() => {
            localConfetti({
              particleCount: 40,
              angle: 60,
              spread: 55,
              origin: { x: 0.15, y: 0.6 },
              colors: particleColors
            });
            localConfetti({
              particleCount: 40,
              angle: 120,
              spread: 55,
              origin: { x: 0.85, y: 0.6 },
              colors: particleColors
            });
          }, 180);
        }

        if (onBoxOpened) onBoxOpened(result);
        if (onCrateOpened) onCrateOpened(result);
      }, 160);

    } catch (e) {
      console.error('[UnboxingModal] Failed to execute unboxing:', e);
      setState('INSPECTING');
    }
  };

  const handleClaim = () => {
    sfx.playUiClick();
    setState('CLAIMED');
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleOpenNext = () => {
    sfx.playUiClick();
    setCurrentQueueIndex(prev => prev + 1);
    setState('INSPECTING');
    setPayout(null);
    setChargeProgress(0);
    setShowOdds(false);
  };

  const renderBoxIcon = (size: string = 'w-16 h-16') => {
    if (boxType === 'perfect_macro') {
      if (isStalker) return <Crown className={cn(size, "text-[#4ade80]")} />;
      return <Crown className={cn(size, "text-emerald-500 dark:text-emerald-400")} />;
    }
    if (boxType === 'streak_matrix') {
      if (isStalker) return <Flame className={cn(size, "text-[#f97316]")} />;
      return <Flame className={cn(size, "text-amber-500 dark:text-amber-400")} />;
    }
    if (isStalker) return <PackageCheck className={cn(size, "text-[#4ade80]")} />;
    return <PackageCheck className={cn(size, "text-emerald-500 dark:text-emerald-400")} />;
  };

  const primaryItem = payout?.items && payout.items.length > 0 ? payout.items[0] : null;

  // Skin and theme-tailored rarity styling
  const getRarityBadgeStyle = (rarity: BioBoxRarity) => {
    if (isStalker) {
      if (rarity === 'mythic' || rarity === 'legendary') {
        return { className: "bg-[#4ade80]/20 border-[#4ade80] text-[#4ade80] font-mono shadow-sm", glow: "rgba(74, 222, 128, 0.6)", hex: "#4ade80" };
      }
      return { className: "bg-black border-[#4ade80]/50 text-[#4ade80] font-mono", glow: "rgba(74, 222, 128, 0.3)", hex: "#4ade80" };
    }
    // Default skin (Light / Dark)
    switch (rarity) {
      case 'mythic':
        return { 
          className: isLight ? "bg-emerald-50 border-emerald-400 text-emerald-800 font-sans font-black shadow-sm" : "bg-emerald-950/70 border-emerald-400 text-emerald-300 font-sans font-black shadow-sm", 
          glow: "rgba(16, 185, 129, 0.55)",
          hex: "#10b981"
        };
      case 'legendary':
        return { 
          className: isLight ? "bg-amber-50 border-amber-400 text-amber-900 font-sans font-black shadow-sm" : "bg-amber-950/70 border-amber-400 text-amber-300 font-sans font-black shadow-sm", 
          glow: "rgba(245, 158, 11, 0.55)",
          hex: "#f59e0b"
        };
      case 'epic':
        return { 
          className: isLight ? "bg-purple-50 border-purple-400 text-purple-900 font-sans font-bold shadow-sm" : "bg-purple-950/70 border-purple-400 text-purple-300 font-sans font-bold shadow-sm", 
          glow: "rgba(168, 85, 247, 0.5)",
          hex: "#a855f7"
        };
      case 'rare':
        return { 
          className: isLight ? "bg-sky-50 border-sky-400 text-sky-900 font-sans font-semibold shadow-sm" : "bg-sky-950/70 border-sky-400 text-sky-300 font-sans font-semibold shadow-sm", 
          glow: "rgba(59, 130, 246, 0.45)",
          hex: "#3b82f6"
        };
      default:
        return { 
          className: isLight ? "bg-zinc-100 border-zinc-300 text-zinc-800 font-sans font-semibold" : "bg-zinc-800 border-zinc-700 text-zinc-200 font-sans font-semibold", 
          glow: "rgba(113, 113, 122, 0.3)",
          hex: "#71717a"
        };
    }
  };

  const activeRarityStyle = payout ? getRarityBadgeStyle(payout.rarity) : getRarityBadgeStyle('common');

  // Dynamic Sunburst / God Rays Color
  const getSunburstGradient = () => {
    if (isStalker) {
      return `conic-gradient(from 0deg at 50% 50%, rgba(74,222,128,0.25) 0deg 20deg, transparent 20deg 40deg, rgba(74,222,128,0.25) 40deg 60deg, transparent 60deg 80deg, rgba(74,222,128,0.25) 80deg 100deg, transparent 100deg 120deg, rgba(74,222,128,0.25) 120deg 140deg, transparent 140deg 160deg, rgba(74,222,128,0.25) 160deg 180deg, transparent 180deg 200deg, rgba(74,222,128,0.25) 200deg 220deg, transparent 220deg 240deg, rgba(74,222,128,0.25) 240deg 260deg, transparent 260deg 280deg, rgba(74,222,128,0.25) 280deg 300deg, transparent 300deg 320deg, rgba(74,222,128,0.25) 320deg 340deg, transparent 340deg 360deg)`;
    }
    return `conic-gradient(from 0deg at 50% 50%, rgba(16,185,129,0.2) 0deg 20deg, transparent 20deg 40deg, rgba(245,158,11,0.15) 40deg 60deg, transparent 60deg 80deg, rgba(16,185,129,0.2) 80deg 100deg, transparent 100deg 120deg, rgba(245,158,11,0.15) 120deg 140deg, transparent 140deg 160deg, rgba(16,185,129,0.2) 160deg 180deg, transparent 180deg 200deg, rgba(245,158,11,0.15) 200deg 220deg, transparent 220deg 240deg, rgba(16,185,129,0.2) 240deg 260deg, transparent 260deg 280deg, rgba(245,158,11,0.15) 280deg 300deg, transparent 300deg 320deg, rgba(16,185,129,0.2) 320deg 340deg, transparent 340deg 360deg)`;
  };

  // Modal styling wrapper classes
  const getModalContainerClasses = () => {
    if (isStalker) {
      return "border-2 border-[#4ade80]/40 bg-black text-[#4ade80] shadow-[0_0_40px_rgba(74,222,128,0.3)] rounded-2xl font-mono";
    }
    return isLight
      ? "border border-zinc-200 bg-white text-zinc-900 shadow-2xl rounded-3xl font-sans"
      : "border border-zinc-800 bg-zinc-900 text-zinc-50 shadow-2xl rounded-3xl font-sans";
  };

  const jitterOffset = (chargeProgress / 100) * 5;

  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn select-none",
      isStalker ? "bg-black/90 backdrop-blur-md" :
      isLight ? "bg-black/50 backdrop-blur-md" : "bg-black/75 backdrop-blur-md"
    )}>
      
      {/* FULL-SCREEN IMPACT SHOCKWAVE EXPANSION */}
      {state === 'IMPACT_FLASH' && (
        <div className="absolute inset-0 pointer-events-none z-50 flex items-center justify-center overflow-hidden">
          <motion.div 
            initial={{ opacity: 0.95 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className={cn(
              "absolute inset-0",
              isStalker ? "bg-[#4ade80]" : "bg-emerald-400"
            )} 
          />
          <motion.div 
            initial={{ scale: 0.2, opacity: 0.9 }}
            animate={{ scale: 2.8, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={cn(
              "w-64 h-64 rounded-full border-4",
              isStalker ? "border-[#4ade80]" : "border-emerald-400"
            )}
          />
        </div>
      )}

      {/* MODAL CONTAINER */}
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: 1,
          x: state === 'PRE_CHARGE' && chargeProgress > 20 
            ? [-(jitterOffset * 0.4), (jitterOffset * 0.4), -(jitterOffset * 0.4), 0] 
            : 0,
          y: state === 'PRE_CHARGE' && chargeProgress > 20 
            ? [(jitterOffset * 0.3), -(jitterOffset * 0.3), (jitterOffset * 0.3), 0] 
            : 0
        }}
        transition={{
          x: { repeat: Infinity, duration: 0.07 },
          y: { repeat: Infinity, duration: 0.07 }
        }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={cn("relative w-full max-w-lg overflow-hidden flex flex-col", getModalContainerClasses())}
      >
        {/* ROTATING SUNBURST BACKGROUND */}
        {(state === 'REVEALING' || state === 'CLAIMED' || (state === 'PRE_CHARGE' && chargeProgress > 40)) && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center opacity-60 z-0">
            <div 
              className="w-[600px] h-[600px] rounded-full animate-spin shrink-0"
              style={{ 
                background: getSunburstGradient(),
                animationDuration: state === 'PRE_CHARGE' ? '6s' : '22s',
                filter: 'blur(1px)'
              }}
            />
          </div>
        )}

        {/* HEADER BAR */}
        <div className={cn(
          "flex items-center justify-between px-5 py-4 border-b relative z-10",
          isStalker ? "border-[#4ade80]/20 bg-black/80 font-mono" :
          isLight ? "border-zinc-100 bg-zinc-50/80 font-sans" : "border-zinc-800 bg-zinc-900/80 font-sans"
        )}>
          <div className="flex items-center gap-2.5 min-w-0">
            <span className={cn(
              "w-2.5 h-2.5 rounded-full animate-pulse shrink-0",
              isStalker ? "bg-[#4ade80] shadow-[0_0_8px_#4ade80]" :
              "bg-emerald-500 shadow-[0_0_8px_#10B981]"
            )} />
            <h3 className={cn(
              "text-xs sm:text-sm font-bold uppercase truncate",
              isStalker ? "font-mono tracking-wider text-[#4ade80]" :
              "text-zinc-900 dark:text-zinc-100 font-sans tracking-tight"
            )}>
              {isStalker
                ? (isUA ? 'КОНТЕЙНЕР ПРИПАСІВ ЗОНИ' : 'ZONE SUPPLY CRATE')
                : (isUA ? 'Розпакування Біо-Боксу' : 'Bio-Box Unboxing')}
              {internalQueue.length > 1 ? ` [${currentQueueIndex + 1}/${internalQueue.length}]` : ''}
            </h3>
          </div>

          <div className="flex items-center gap-2">
            {/* Close Button */}
            {state !== 'PRE_CHARGE' && state !== 'IMPACT_FLASH' && (
              <button
                type="button"
                onClick={() => {
                  sfx.playUiClick();
                  onClose();
                }}
                className={cn(
                  "p-1.5 rounded-lg border transition-colors cursor-pointer",
                  isStalker ? "border-[#4ade80]/30 text-[#4ade80]/70 hover:text-[#4ade80] hover:bg-[#4ade80]/10 font-mono" :
                  isLight ? "border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 font-sans" : "border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 font-sans"
                )}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* MODAL BODY */}
        <div className="p-6 flex flex-col items-center justify-center text-center relative min-h-[390px] z-10">
          
          {/* STAGE 1 & 2: INSPECTING & PRE_CHARGE */}
          {(state === 'INSPECTING' || state === 'PRE_CHARGE') && (
            <div className="w-full flex flex-col items-center space-y-5 animate-fadeIn">
              
              {/* Box Visual Container with Ambient Aura */}
              <div className="relative my-3 flex items-center justify-center">
                <div 
                  className={cn(
                    "absolute -inset-6 rounded-full transition-all duration-500 filter blur-xl",
                    state === 'PRE_CHARGE' ? "opacity-80 scale-125" : "opacity-30 scale-90",
                    isStalker ? "bg-[#4ade80]/40" : "bg-emerald-500/35"
                  )}
                />

                {/* Counter-rotating energy rings during precharge */}
                {state === 'PRE_CHARGE' && (
                  <>
                    <div 
                      className={cn(
                        "absolute -inset-4 rounded-3xl border-2 border-dashed animate-spin pointer-events-none",
                        isStalker ? "border-[#4ade80]/60" : "border-emerald-400/80"
                      )} 
                      style={{ animationDuration: '2.4s' }} 
                    />
                    <div 
                      className={cn(
                        "absolute -inset-8 rounded-full border border-dotted animate-spin pointer-events-none",
                        isStalker ? "border-amber-400/50" : "border-teal-400/50"
                      )} 
                      style={{ animationDuration: '4s', animationDirection: 'reverse' }} 
                    />
                  </>
                )}

                {/* Main Box Visual Item */}
                <motion.div 
                  animate={
                    state === 'PRE_CHARGE' 
                      ? {
                          scale: [1, 1.06 + (chargeProgress / 400), 1.03],
                          rotate: [-(jitterOffset), jitterOffset, -(jitterOffset), 0],
                          x: [-(jitterOffset * 1.5), jitterOffset * 1.5, -(jitterOffset * 1.5), 0]
                        }
                      : {
                          y: [-4, 4, -4],
                          rotate: [-1, 1, -1]
                        }
                  }
                  transition={
                    state === 'PRE_CHARGE'
                      ? { repeat: Infinity, duration: Math.max(0.04, 0.16 - (chargeProgress / 1000)) }
                      : { repeat: Infinity, duration: 4, ease: "easeInOut" }
                  }
                  className={cn(
                    "w-32 h-32 sm:w-36 sm:h-36 rounded-3xl flex items-center justify-center border-2 transition-all relative overflow-hidden",
                    isStalker ? (
                      state === 'PRE_CHARGE'
                        ? "border-[#4ade80] shadow-[0_0_50px_rgba(74,222,128,0.7)] bg-black"
                        : "border-[#4ade80]/40 bg-zinc-950 shadow-[0_0_25px_rgba(74,222,128,0.25)]"
                    ) : (
                      state === 'PRE_CHARGE'
                        ? "border-emerald-500 shadow-[0_0_45px_rgba(16,185,129,0.5)] bg-emerald-500/15"
                        : isLight
                        ? "border-emerald-200 bg-gradient-to-br from-emerald-50/80 via-white to-teal-50/50 shadow-md"
                        : "border-emerald-500/30 bg-zinc-800/90 shadow-lg"
                    )
                  )}
                >
                  <motion.div 
                    animate={{ x: ['-120%', '220%'] }}
                    transition={{ repeat: Infinity, duration: state === 'PRE_CHARGE' ? 0.8 : 3.5, ease: "linear" }}
                    className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 pointer-events-none"
                  />

                  {renderBoxIcon("w-16 h-16 sm:w-20 sm:h-20")}
                </motion.div>
              </div>

              {/* Bio-Box Information */}
              <div className="space-y-1.5 max-w-sm">
                <span className={cn(
                  "text-[10px] uppercase tracking-widest px-2.5 py-0.5 rounded-full inline-block font-semibold",
                  isStalker ? "font-mono text-[#4ade80]/80 bg-black border border-[#4ade80]/30" :
                  isLight ? "text-emerald-700 bg-emerald-50 border border-emerald-200 font-sans" : "text-emerald-400 bg-emerald-950/50 border border-emerald-800/60 font-sans"
                )}>
                  {activeBox.source}
                </span>
                <h4 className={cn(
                  "text-xl sm:text-2xl font-black uppercase tracking-wider",
                  isStalker ? "text-[#4ade80] font-mono" :
                  "text-zinc-900 dark:text-zinc-100 font-sans tracking-tight"
                )}>
                  {isUA ? boxMeta.name.ua : boxMeta.name.en}
                </h4>
                <p className={cn(
                  "text-xs leading-relaxed",
                  isStalker ? "text-[#4ade80]/80 font-mono" :
                  isLight ? "text-zinc-600 font-sans" : "text-zinc-400 font-sans"
                )}>
                  {tone === 'bro' && (isUA ? boxMeta.tagline.broUA : boxMeta.tagline.broEN)
                    ? (isUA ? boxMeta.tagline.broUA : boxMeta.tagline.broEN)
                    : (isUA ? boxMeta.tagline.ua : boxMeta.tagline.en)}
                </p>
              </div>

              {/* Pre-Charge Status Bar */}
              {state === 'PRE_CHARGE' ? (
                <div className="w-full max-w-xs space-y-2 pt-2">
                  <div className={cn(
                    "flex justify-between text-[11px] font-bold",
                    isStalker ? "font-mono text-[#4ade80]" :
                    isLight ? "text-emerald-700 font-sans" : "text-emerald-400 font-sans"
                  )}>
                    <span>
                      {isStalker ? (isUA ? 'СИНХРОНІЗАЦІЯ КОНТЕЙНЕРА...' : 'SYNCHRONIZING CONTAINER...') :
                       (isUA ? 'Розпакування...' : 'Unboxing...')}
                    </span>
                    <span>{chargeProgress}%</span>
                  </div>
                  <div className={cn(
                    "w-full h-2.5 rounded-full overflow-hidden p-0.5 border relative shadow-inner",
                    isStalker ? "bg-black border-[#4ade80]/40 font-mono" :
                    isLight ? "bg-zinc-100 border-zinc-200" : "bg-zinc-800 border-zinc-700"
                  )}>
                    <div 
                      className={cn(
                        "h-full rounded-full transition-all duration-75 relative overflow-hidden",
                        isStalker ? "bg-gradient-to-r from-[#15803d] via-[#4ade80] to-[#86efac]" :
                        "bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-300 shadow-[0_0_12px_#10B981]"
                      )}
                      style={{ width: `${chargeProgress}%` }}
                    >
                      <motion.div 
                        animate={{ x: ['-100%', '200%'] }}
                        transition={{ repeat: Infinity, duration: 0.6, ease: "linear" }}
                        className="absolute inset-0 bg-white/40 skew-x-12"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Primary Action Trigger */
                <div className="w-full max-w-xs space-y-3 pt-2">
                  <button
                    type="button"
                    onClick={handleStartUnboxing}
                    className={cn(
                      "w-full py-3.5 px-6 rounded-2xl font-bold uppercase tracking-wider text-sm transition-all cursor-pointer flex items-center justify-center gap-2",
                      isStalker ? "bg-[#4ade80]/20 hover:bg-[#4ade80]/30 border border-[#4ade80] text-[#4ade80] font-mono shadow-[0_0_20px_rgba(74,222,128,0.35)] hover:scale-[1.02] active:scale-95" :
                      "bg-emerald-500 hover:bg-emerald-600 text-white font-sans shadow-lg shadow-emerald-500/30 hover:scale-[1.02] active:scale-95 font-semibold"
                    )}
                  >
                    <Zap className="w-4 h-4 fill-current animate-pulse" />
                    <span>
                      {isStalker ? (isUA ? 'ВІДКРИТИ КОНТЕЙНЕР' : 'OPEN SUPPLY CRATE') :
                       (isUA ? 'РОЗПАКУВАТИ БІО-БОКС' : 'UNBOX BIO-BOX')}
                    </span>
                  </button>

                  {/* Probability Drop Odds Toggle */}
                  <button
                    type="button"
                    onClick={() => {
                      sfx.playUiClick();
                      setShowOdds(!showOdds);
                    }}
                    className={cn(
                      "flex items-center justify-center gap-1.5 text-[11px] transition-colors mx-auto cursor-pointer",
                      isStalker ? "text-[#4ade80]/70 hover:text-[#4ade80] font-mono" :
                      isLight ? "text-zinc-500 hover:text-emerald-600 font-sans" : "text-zinc-400 hover:text-emerald-400 font-sans"
                    )}
                  >
                    <Info className="w-3.5 h-3.5" />
                    <span>{showOdds ? (isUA ? 'Сховати ймовірності' : 'Hide Drop Rates') : (isUA ? 'Переглянути ймовірності' : 'Inspect Probability Odds')}</span>
                  </button>

                  {showOdds && (
                    <div className={cn(
                      "p-3.5 rounded-2xl border text-[11px] space-y-1.5 text-left animate-fadeIn",
                      isStalker ? "bg-black border border-[#4ade80]/30 font-mono text-[#4ade80]" :
                      isLight ? "bg-zinc-50 border-zinc-200 text-zinc-700 font-sans" : "bg-zinc-800/80 border-zinc-700 text-zinc-300 font-sans"
                    )}>
                      <div className="flex justify-between text-slate-500 dark:text-slate-400 font-medium"><span>Common (Звичайний):</span><span>55.0%</span></div>
                      <div className="flex justify-between text-sky-600 dark:text-sky-400 font-medium"><span>Rare (Рідкісний):</span><span>28.0%</span></div>
                      <div className="flex justify-between text-purple-600 dark:text-purple-400 font-semibold"><span>Epic (Епічний):</span><span>12.0%</span></div>
                      <div className="flex justify-between text-amber-600 dark:text-amber-400 font-bold"><span>Legendary (Легендарний):</span><span>4.2%</span></div>
                      <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-bold"><span>Mythic (Міфічний):</span><span>0.8%</span></div>
                    </div>
                  )}
                </div>
              )}

            </div>
          )}

          {/* STAGE 4 & 5: REVEALING & CLAIMED PAYOUT CARD FLIP */}
          {(state === 'REVEALING' || state === 'CLAIMED') && payout && (
            <motion.div 
              initial={{ scale: 0.15, rotateY: 180, rotateZ: -10, opacity: 0 }}
              animate={{ scale: 1, rotateY: 0, rotateZ: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 180, damping: 16 }}
              className="w-full flex flex-col items-center space-y-5 animate-fadeIn relative z-20"
            >
              
              {/* Rarity Tag Banner */}
              <motion.div 
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15, type: "spring" }}
                className={cn(
                  "px-4 py-1.5 rounded-full border text-[11px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-md",
                  activeRarityStyle.className
                )}
              >
                <Sparkles className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '4s' }} />
                <span>
                  {isStalker 
                    ? `${payout.rarity.toUpperCase()} ${isUA ? 'ХАБАР' : 'STASH'}` 
                    : `${payout.rarity.toUpperCase()} ${isUA ? 'НАГОРОДА' : 'PAYOUT'}`}
                </span>
              </motion.div>

              {/* Center Item Card Preview */}
              <motion.div 
                animate={{ y: [-3, 3, -3] }}
                transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
                className={cn(
                  "w-40 h-40 rounded-3xl flex flex-col items-center justify-center p-3 border-2 transition-all relative overflow-hidden",
                  isStalker ? (
                    "bg-black border-[#4ade80] shadow-[0_0_35px_rgba(74,222,128,0.4)]"
                  ) : (
                    isLight 
                      ? "bg-white border-zinc-200 shadow-2xl" 
                      : "bg-zinc-900 border-zinc-700 shadow-2xl"
                  )
                )}
              >
                <motion.div 
                  animate={{ x: ['-140%', '240%'] }}
                  transition={{ repeat: Infinity, duration: 2.8, ease: "easeInOut" }}
                  className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-12 pointer-events-none"
                />

                {primaryItem?.type === 'pro_voucher_7d' || primaryItem?.type === 'pro_voucher_48h' || primaryItem?.type === 'pro_voucher_24h' ? (
                  <Crown className={cn("w-16 h-16 drop-shadow-lg", isStalker ? "text-[#4ade80]" : "text-amber-500 dark:text-amber-400")} />
                ) : primaryItem?.type === 'streak_freeze' ? (
                  <Shield className={cn("w-16 h-16 drop-shadow-lg", isStalker ? "text-[#4ade80]" : "text-blue-500 dark:text-blue-400")} />
                ) : primaryItem?.type === 'theme_unlock' ? (
                  <Palette className={cn("w-16 h-16 drop-shadow-lg", isStalker ? "text-[#4ade80]" : "text-purple-500 dark:text-purple-400")} />
                ) : (
                  <Coins className={cn("w-16 h-16 animate-spin drop-shadow-lg", isStalker ? "text-[#4ade80]" : "text-amber-500 dark:text-amber-400")} style={{ animationDuration: '6s' }} />
                )}

                <span className={cn(
                  "text-[11px] font-black mt-2 truncate max-w-[130px] uppercase tracking-wide",
                  isStalker ? "font-mono text-[#4ade80]" :
                  "text-zinc-900 dark:text-white font-sans"
                )}>
                  {primaryItem ? (isUA ? primaryItem.name.ua : primaryItem.name.en) : 'Nutri-Tokens'}
                </span>
              </motion.div>

              {/* Token & Item Descriptor */}
              <div className="space-y-2 max-w-sm">
                <div className={cn(
                  "inline-flex items-center gap-2 px-4 py-1.5 rounded-xl border font-bold text-sm shadow-sm",
                  isStalker ? "bg-[#4ade80]/15 border-[#4ade80]/40 text-[#4ade80] font-mono" :
                  isLight ? "bg-emerald-50 border-emerald-200 text-emerald-700 font-sans" : "bg-emerald-950/50 border-emerald-800/60 text-emerald-400 font-sans"
                )}>
                  <Coins className="w-4 h-4 text-amber-500" />
                  <span>+{payout.tokenPayout} Nutri-Tokens</span>
                </div>

                <p className={cn(
                  "text-xs leading-relaxed",
                  isStalker ? "text-[#4ade80]/80 font-mono" :
                  isLight ? "text-zinc-600 font-sans" : "text-zinc-400 font-sans"
                )}>
                  {tone === 'bro' && primaryItem?.description
                    ? (isUA ? primaryItem.description.broFlavorUA : primaryItem.description.broFlavorEN) || (isUA ? primaryItem.description.ua : primaryItem.description.en)
                    : primaryItem ? (isUA ? primaryItem.description.ua : primaryItem.description.en) : ''}
                </p>
              </div>

              {/* Claim Action Button */}
              <div className="w-full max-w-xs pt-2 space-y-2">
                {currentQueueIndex < internalQueue.length - 1 ? (
                  <>
                    <button
                      type="button"
                      onClick={handleOpenNext}
                      className={cn(
                        "w-full py-3.5 px-6 rounded-2xl font-bold uppercase tracking-wider text-sm transition-all cursor-pointer flex items-center justify-center gap-2",
                        isStalker ? "bg-[#4ade80]/20 hover:bg-[#4ade80]/30 border border-[#4ade80] text-[#4ade80] font-mono shadow-[0_0_20px_rgba(74,222,128,0.35)] hover:scale-[1.02] active:scale-95" :
                        "bg-emerald-500 hover:bg-emerald-600 text-white font-sans shadow-lg shadow-emerald-500/30 hover:scale-[1.02] active:scale-95 font-semibold"
                      )}
                    >
                      <Zap className="w-4 h-4 fill-current" />
                      <span>{isUA ? `ВІДКРИТИ НАСТУПНИЙ (${internalQueue.length - 1 - currentQueueIndex})` : `OPEN NEXT BOX (${internalQueue.length - 1 - currentQueueIndex} LEFT)`}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleClaim}
                      className={cn(
                        "w-full py-2.5 px-4 rounded-xl uppercase tracking-wider text-xs transition-colors cursor-pointer border flex items-center justify-center gap-1.5 font-medium",
                        isStalker ? "bg-black hover:bg-zinc-900 text-[#4ade80]/60 hover:text-[#4ade80] border-[#4ade80]/20 font-mono" :
                        isLight ? "bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border-zinc-200 font-sans" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700 font-sans"
                      )}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{isUA ? 'Забрати та закрити' : 'Claim & Close'}</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleClaim}
                    className={cn(
                      "w-full py-3.5 px-6 rounded-2xl font-bold uppercase tracking-wider text-sm transition-all cursor-pointer flex items-center justify-center gap-2",
                      isStalker ? "bg-[#4ade80]/20 hover:bg-[#4ade80]/30 border border-[#4ade80] text-[#4ade80] font-mono shadow-[0_0_20px_rgba(74,222,128,0.35)] hover:scale-[1.02] active:scale-95" :
                      "bg-emerald-500 hover:bg-emerald-600 text-white font-sans shadow-lg shadow-emerald-500/30 hover:scale-[1.02] active:scale-95 font-semibold"
                    )}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>
                      {isStalker ? (isUA ? 'ЗАБРАТИ У СТАШ' : 'STASH REWARDS') :
                       (isUA ? 'ЗАБРАТИ НАГОРОДИ' : 'CLAIM REWARDS')}
                    </span>
                  </button>
                )}
              </div>

            </motion.div>
          )}

        </div>

      </motion.div>
    </div>
  );
};

